export const AUTO_VALUE = "auto";

const CSS_UNIT_PATTERN = /^\d+(\.\d+)?(rem|px|em|ch|ex|vh|vw|%)$/;

/**
 * Check if a value is a valid CSS unit string
 * @param {string} value - value to check
 * @returns {boolean}
 *
 * @example
 * isCssUnit("1.5rem") // true
 * isCssUnit("24px") // true
 * isCssUnit("small") // false
 */
export function isCssUnit(value) {
  return CSS_UNIT_PATTERN.test(value);
}

function toRem(value) {
  return value / 16;
}

export function toRemString(value) {
  if (value === AUTO_VALUE) {
    return value;
  }

  return `${toRem(value)}rem`;
}

/**
 * Parses a CSS-like padding string (e.g. "2.5rem 1rem 2.5rem 0")
 * into an object of CSS custom properties.
 *
 * Supports 1–4 values (CSS shorthand logic):
 * 1 → all sides
 * 2 → top/bottom | left/right
 * 3 → top | left/right | bottom
 * 4 → top | right | bottom | left
 *
 * @param {string} input
 * @returns {Record<string, string>}
 *
 * @example
 * parsePadding("2.5rem 1rem 2.5rem 0")
 * // returns { top: "2.5rem", right: "1rem", bottom: "2.5rem", left: "0" }
 * parsePadding("2.5rem 1rem 2.5rem")
 * // returns { top: "2.5rem", right: "1rem", bottom: "2.5rem", left: "1rem" }
 * parsePadding("2.5rem")
 * // returns { top: "2.5rem", right: "2.5rem", bottom: "2.5rem", left: "2.5rem" }
 * parsePadding("2.5rem 1rem")
 * // returns { top: "2.5rem", right: "1rem", bottom: "2.5rem", left: "1rem" }
 */
export function parsePadding(input = "") {
  const parts = input?.trim?.().split(/\s+/).filter(Boolean);

  if (!parts || parts.length === 0) {
    return {};
  }

  const all = parts[0];

  const top = all;
  const right = parts[1] ?? all;
  const bottom = parts[2] ?? all;
  const left = parts[3] ?? right;

  return {
    top,
    right,
    bottom,
    left,
  };
}

// Computed font-size of an element in px; falls back to 16 only when unresolved
// (NaN, e.g. jsdom), so a genuine 0 stays 0.
function fontSizePx(element) {
  const size = parseFloat(getComputedStyle(element).fontSize);
  return Number.isFinite(size) ? size : 16;
}

const expressionLengths = new WeakMap();
const EXPRESSION = /^(?:calc|var|min|max|clamp)\(/i;

/**
 * Clears cached expression lengths for an element.
 * @param {Element} element
 */
export function resetResolvedLengths(element) {
  expressionLengths.delete(element);
}

function expressionLengthPx(value, relativeTo, relatedRaw) {
  const target = relativeTo ?? document.documentElement;
  if (target === document.body) {
    return null;
  }
  let values = expressionLengths.get(target);
  if (!values) {
    values = new Map();
    expressionLengths.set(target, values);
  }
  if (values.has(value)) {
    return values.get(value);
  }
  const pending = [...new Set([value, relatedRaw?.trim()])].filter(
    (raw) => raw && EXPRESSION.test(raw) && !values.has(raw),
  );
  const probe = target.ownerDocument.createElement("span");
  probe.style.cssText = "position:absolute;width:0;height:0;visibility:hidden;pointer-events:none;padding:0;border:0";
  const properties = ["marginLeft", "marginTop"];
  for (const [index, raw] of pending.entries()) {
    probe.style.setProperty(`--custom-element-length-${index}`, raw);
    probe.style[properties[index]] = `var(--custom-element-length-${index})`;
  }
  target.appendChild(probe);
  try {
    const style = getComputedStyle(probe);
    for (const [index, raw] of pending.entries()) {
      const resolved = style.getPropertyValue(`--custom-element-length-${index}`).trim();
      const measured = style[properties[index]];
      const valid = resolved && (typeof CSS === "undefined" || CSS.supports("margin-left", resolved));
      values.set(raw, valid && /^-?\d*\.?\d+(?:e[+-]?\d+)?px$/i.test(measured) ? Number.parseFloat(measured) : null);
    }
  } finally {
    probe.remove();
  }
  return values.get(value);
}

/**
 * Resolve a CSS length token or math expression to pixels. Custom
 * properties come back from getComputedStyle as their authored string, never
 * resolved, so rem/em are converted manually against the relevant font size.
 * Expressions are measured in the given element and cached until resetResolvedLengths clears them.
 *
 * @param {string} raw - the length token, e.g. "1.5rem", "12px", "4".
 * @param {Element} [relativeTo] - element whose font-size resolves `em` units and inside which expressions are measured; defaults to the document root. Expressions measured against `document.body` return null.
 * @param {string} [relatedRaw] - a second expression measured alongside this one
 * @returns {number|null} pixels, or null when the value cannot be resolved.
 *
 * @example
 * resolveLengthToPx("1rem")    // 16 (at a 16px root)
 * resolveLengthToPx("12px")    // 12
 * resolveLengthToPx("calc(1rem + 4px)") // 20 (at a 16px root)
 */
export function resolveLengthToPx(raw, relativeTo, relatedRaw) {
  const value = raw?.trim();
  if (!value) {
    return null;
  }
  const num = parseFloat(value);
  if (!Number.isFinite(num)) {
    return EXPRESSION.test(value) ? expressionLengthPx(value, relativeTo, relatedRaw) : null;
  }
  if (value.endsWith("rem")) {
    return num * fontSizePx(document.documentElement);
  }
  if (value.endsWith("em")) {
    return num * fontSizePx(relativeTo ?? document.documentElement);
  }
  if (value.endsWith("px")) {
    return num;
  }
  // Unitless (e.g. "4") is treated as px; any other unit (%, vh, vw, ch, …) is
  // unsupported and returns null so callers fall back to their default.
  return /^-?\d*\.?\d+$/.test(value) ? num : null;
}
